import React, { useEffect, useRef, useState } from 'react';
import { obtenerNegocio, actualizarNegocio, normalizarSlug } from '../services/negociosService';
import FacturacionConfig from './FacturacionConfig';
import { subirLogo } from '../services/fotosService';
import { setCodigoPais, setMoneda, simboloMoneda } from '../services/negocioActual';
import { codigoTelefonoPorPais, ZONAS_HORARIAS, MONEDAS, monedaLabel, monedaPorPais, zonaHorariaPorPais } from '../utils/paises';
import { PAISES, RUBROS } from '../utils/onboarding';
import { mensajeAmigable } from '../utils/errores';
import CampoNumero from './CampoNumero';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import { obtenerPanel, guardarPanel, aplicarPanel } from '../services/tema';
import MisRedesSociales from './MisRedesSociales';
import DisenoTiendaConfig from './DisenoTiendaConfig';
import { normalizarDiseno } from '../utils/tiendaDiseno';
import { normalizarEnvio } from '../utils/tiendaEnvio';
import { normalizarPago } from '../utils/tiendaPago';
import EnvioTiendaConfig from './EnvioTiendaConfig';
import CombosTiendaConfig from './CombosTiendaConfig';
import { OPCIONES_VENDE } from '../utils/queVende';
import { normalizarCombos } from '../utils/tiendaCombos';

// Colores de marca sugeridos (todos con buen contraste para texto blanco).
const COLORES = [
  { nombre: 'Rosa', valor: '#DB2777' },
  { nombre: 'Violeta', valor: '#7C3AED' },
  { nombre: 'Amatista', valor: '#9333EA' },
  { nombre: 'Azul', valor: '#2563EB' },
  { nombre: 'Turquesa', valor: '#0D9488' },
  { nombre: 'Verde', valor: '#059669' },
  { nombre: 'Vino', valor: '#BE123C' },
  { nombre: 'Naranja', valor: '#EA580C' },
  { nombre: 'Dorado', valor: '#CA8A04' },
  { nombre: 'Negro', valor: '#111827' },
];

const COLOR_DEFECTO = '#DB2777';

// Panel de configuración del negocio: nombre, teléfono/WhatsApp, país,
// moneda, LOGO y COLOR de marca (personalización por negocio).
export default function ConfiguracionView({ onVolver }) {
  const { mostrarToast } = useToast();
  const { actualizarNegocioLocal } = useAuth();
  const [datos, setDatos] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [portadaFile, setPortadaFile] = useState(null);
  const [portadaPreview, setPortadaPreview] = useState(null);
  const [error, setError] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const colorOriginalRef = useRef(COLOR_DEFECTO);
  const guardadoRef = useRef(false);
  const [panel, setPanel] = useState(obtenerPanel());
  const panelOriginalRef = useRef(obtenerPanel());

  useEffect(() => {
    let activo = true;
    obtenerNegocio()
      .then((n) => {
        if (!activo) return;
        colorOriginalRef.current = n.color_principal || COLOR_DEFECTO;
        setDatos({
          nombre: n.nombre || '',
          telefono: n.telefono || '',
          instagram: n.instagram || '',
          pais: n.pais || 'Honduras',
          moneda: n.moneda || 'HNL',
          zona_horaria: n.zona_horaria || 'America/Tegucigalpa',
          logo_url: n.logo_url || null,
          color_principal: n.color_principal || COLOR_DEFECTO,
          comision_pos_pct: (n.comision_pos_pct ?? 4).toString(),
          catalogo_publico: !!n.catalogo_publico,
          meta_pixel_id: n.meta_pixel_id || '',
          slug: n.slug || '',
          modo_viajes: !!n.modo_viajes,
          rubro: n.rubro || '',
          vende: n.vende || 'ambos',
          razon_social: n.razon_social || '',
          rtn: n.rtn || '',
          direccion: n.direccion || '',
          correo: n.correo || '',
          pie_comprobante: n.pie_comprobante || '',
          portada_url: n.portada_url || null,
          catalogo_bienvenida: n.catalogo_bienvenida || '',
          tienda_diseno: n.tienda_diseno || {},
          tienda_envio: normalizarEnvio(n.tienda_envio),
          tienda_pago: normalizarPago(n.tienda_pago),
          tienda_combos: n.tienda_combos && Array.isArray(n.tienda_combos.combos) ? n.tienda_combos : { combos: [] },
        });
      })
      .catch((e) => activo && setError(mensajeAmigable(e)));
    return () => { activo = false; };
  }, []);

  // Si el usuario sale sin guardar, restaura el color original (la vista
  // previa en vivo no debe quedar aplicada).
  useEffect(() => {
    return () => {
      if (!guardadoRef.current) {
        document.documentElement.style.setProperty('--color-primario', colorOriginalRef.current);
        aplicarPanel(panelOriginalRef.current);
      }
    };
  }, []);

  const set = (campo, valor) => setDatos((d) => ({ ...d, [campo]: valor }));

  // Al elegir país, sugiere sola la moneda y la zona horaria de ese país
  // (el usuario todavía puede cambiarlas antes de guardar).
  const elegirPais = (pais) => {
    setDatos((d) => {
      const nuevo = { ...d, pais };
      const moneda = monedaPorPais(pais);
      if (moneda) nuevo.moneda = moneda;
      if (pais) nuevo.zona_horaria = zonaHorariaPorPais(pais);
      return nuevo;
    });
  };

  // Vista previa en vivo del estilo del panel (morado / lavanda).
  const elegirPanel = (estilo) => {
    setPanel(estilo);
    aplicarPanel(estilo);
  };

  // Vista previa en vivo del color en toda la app.
  const elegirColor = (valor) => {
    set('color_principal', valor);
    document.documentElement.style.setProperty('--color-primario', valor);
  };

  const elegirLogo = (archivo) => {
    if (!archivo) return;
    setLogoFile(archivo);
    setLogoPreview(URL.createObjectURL(archivo));
  };

  const guardar = async () => {
    if (!datos.nombre.trim()) {
      mostrarToast('El nombre del negocio no puede estar vacío', 'error');
      return;
    }
    setGuardando(true);
    try {
      let logo_url = datos.logo_url;
      if (logoFile) {
        logo_url = await subirLogo(logoFile);
      }
      let portada_url = datos.portada_url;
      if (portadaFile) portada_url = await subirLogo(portadaFile);
      await actualizarNegocio({ ...datos, logo_url, portada_url, tienda_diseno: normalizarDiseno(datos.tienda_diseno), tienda_envio: normalizarEnvio(datos.tienda_envio), tienda_pago: normalizarPago(datos.tienda_pago), tienda_combos: normalizarCombos(datos.tienda_combos, simboloMoneda(datos.moneda)) });
      // Refleja nombre, logo y color en el header/app sin recargar.
      actualizarNegocioLocal({ nombre: datos.nombre.trim(), logo_url, color_principal: datos.color_principal, slug: normalizarSlug(datos.slug) || null, rubro: datos.rubro || null, modo_viajes: !!datos.modo_viajes, vende: datos.vende || 'ambos' });
      // Ajusta el código de WhatsApp y la moneda según el país, al instante.
      setCodigoPais(codigoTelefonoPorPais(datos.pais));
      setMoneda(datos.moneda);
      colorOriginalRef.current = datos.color_principal;
      guardarPanel(panel);
      panelOriginalRef.current = panel;
      guardadoRef.current = true;
      setLogoFile(null);
      set('logo_url', logo_url);
      setPortadaFile(null);
      set('portada_url', portada_url);
      mostrarToast('¡Configuración guardada! ✅', 'exito');
    } catch (e) {
      mostrarToast(mensajeAmigable(e), 'error');
    } finally {
      setGuardando(false);
    }
  };

  const input = { width: '100%', padding: '11px', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' };
  const label = { display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 600, color: '#374151' };
  const logoMostrado = logoPreview || datos?.logo_url;

  return (
    <div style={{ maxWidth: '640px', margin: '0 auto', padding: '24px' }}>
      <div className="k-page-head">
        <h2 className="k-page-title">⚙️ Configuración del negocio</h2>
        <button onClick={onVolver} className="k-btn-soft">← Volver</button>
      </div>

      {error && (
        <div style={{ padding: '16px', backgroundColor: '#FEE2E2', borderRadius: '12px', border: '1px solid #FCA5A5', color: '#991B1B', fontWeight: 600 }}>⚠️ {error}</div>
      )}

      {!datos && !error && (
        <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>⏳ Cargando...</div>
      )}

      {datos && <MisRedesSociales />}

      {datos && (
        <div style={{ backgroundColor: 'white', borderRadius: '14px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', border: '1px solid #F3F4F6' }}>
          <div style={{ marginBottom: '16px' }}>
            <label style={label}>Nombre del negocio *</label>
            <input type="text" value={datos.nombre} disabled={guardando}
              onChange={(e) => set('nombre', e.target.value)} style={input} placeholder="Ej: Lolita's Store" />
          </div>

          {/* Logo */}
          <div style={{ marginBottom: '20px' }}>
            <label style={label}>Logo del negocio</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
              <div style={{ width: '64px', height: '64px', borderRadius: '12px', border: '1px solid #E5E7EB', backgroundColor: '#F9FAFB', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
                {logoMostrado ? <img src={logoMostrado} alt="logo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ fontSize: '28px' }}>🛍️</span>}
              </div>
              <div>
                <input type="file" accept="image/*" disabled={guardando}
                  onChange={(e) => elegirLogo(e.target.files[0])}
                  style={{ fontSize: '13px' }} />
                <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#9CA3AF' }}>Aparece en el encabezado de tu app. Cuadrado se ve mejor.</p>
              </div>
            </div>
          </div>

          {/* Color de marca */}
          <div style={{ marginBottom: '20px' }}>
            <label style={label}>Color de tu marca</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '10px' }}>
              {COLORES.map((c) => {
                const activo = datos.color_principal?.toUpperCase() === c.valor.toUpperCase();
                return (
                  <button key={c.valor} type="button" title={c.nombre} disabled={guardando}
                    onClick={() => elegirColor(c.valor)}
                    style={{ width: '38px', height: '38px', borderRadius: '10px', backgroundColor: c.valor, cursor: 'pointer',
                      border: activo ? '3px solid #111827' : '2px solid white', boxShadow: '0 0 0 1px #E5E7EB' }} />
                );
              })}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <input type="color" value={datos.color_principal || COLOR_DEFECTO} disabled={guardando}
                onChange={(e) => elegirColor(e.target.value)}
                style={{ width: '46px', height: '38px', border: '1px solid #D1D5DB', borderRadius: '8px', cursor: 'pointer', padding: 0, background: 'none' }} />
              <span style={{ fontSize: '13px', color: '#6B7280' }}>O elige un color exacto · <strong>{datos.color_principal}</strong></span>
            </div>
            <p style={{ margin: '8px 0 0', fontSize: '12px', color: '#9CA3AF' }}>Se aplica al instante para que veas cómo queda. Guarda para dejarlo fijo.</p>
          </div>

          {/* Estilo del panel: morado o lavanda (esencia Automatika) */}
          <div style={{ marginBottom: '20px' }}>
            <label style={label}>Estilo del panel lateral</label>
            <div style={{ display: 'flex', gap: '10px' }}>
              {[
                { id: 'violet', nombre: 'Morado', grad: 'linear-gradient(150deg,#241a52,#4a1d84 55%,#7a1e74)' },
                { id: 'lavender', nombre: 'Lavanda', grad: 'linear-gradient(150deg,#F4EEFF,#E7DAFA 55%,#F1DFF6)' },
              ].map((op) => {
                const activo = panel === op.id;
                return (
                  <button key={op.id} type="button" disabled={guardando} onClick={() => elegirPanel(op.id)}
                    style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', cursor: 'pointer',
                      borderRadius: '12px', background: 'white', textAlign: 'left',
                      border: activo ? '2px solid var(--color-primario,#7C3AED)' : '1px solid #D1D5DB' }}>
                    <span style={{ width: '34px', height: '34px', borderRadius: '9px', background: op.grad, flexShrink: 0, boxShadow: '0 0 0 1px rgba(0,0,0,0.05)' }} />
                    <span style={{ fontSize: '13.5px', fontWeight: 700, color: '#374151' }}>{op.nombre}{activo ? ' ✓' : ''}</span>
                  </button>
                );
              })}
            </div>
            <p style={{ margin: '8px 0 0', fontSize: '12px', color: '#9CA3AF' }}>Elige cómo se ve tu panel. La marca Automatika se mantiene siempre; solo cambia el tono.</p>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={label}>Teléfono / WhatsApp</label>
            <input type="tel" value={datos.telefono} disabled={guardando}
              onChange={(e) => set('telefono', e.target.value)} style={input} placeholder="Ej: +504 9999-9999" />
            <p style={{ margin: '5px 0 0', fontSize: '12px', color: '#9CA3AF' }}>Para que tus clientas te contacten desde el catálogo.</p>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={label}>Usuario de Instagram</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontSize: '15px', color: '#9CA3AF', fontWeight: 600 }}>@</span>
              <input type="text" value={datos.instagram} disabled={guardando}
                onChange={(e) => set('instagram', e.target.value)} style={input} placeholder="Ej: lalolitas_store.hn" />
            </div>
            <p style={{ margin: '5px 0 0', fontSize: '12px', color: '#9CA3AF' }}>El botón 📷 Instagram del catálogo abrirá tus mensajes de Instagram. Escribe solo el usuario (sin @).</p>
          </div>

          <div style={{ marginBottom: '16px', padding: '14px', background: '#FAF8FF', border: '1px solid #EDE9FE', borderRadius: '12px' }}>
            <div style={{ fontWeight: 700, marginBottom: '4px' }}>🧾 Datos para tus comprobantes de venta</div>
            <p style={{ margin: '0 0 12px', fontSize: '12px', color: '#6B7280' }}>Salen en el comprobante que le das a tus clientes junto con tu logo. Todos son opcionales.</p>
            <label style={label}>Nombre legal o razón social</label>
            <input type="text" value={datos.razon_social} disabled={guardando} maxLength={120}
              onChange={(e) => set('razon_social', e.target.value)} style={{ ...input, marginBottom: '10px' }} placeholder="Ej: Inversiones Lolita S. de R.L." />
            <label style={label}>RTN</label>
            <input type="text" inputMode="numeric" value={datos.rtn} disabled={guardando} maxLength={20}
              onChange={(e) => set('rtn', e.target.value)} style={{ ...input, marginBottom: '10px' }} placeholder="Ej: 0801-1990-123456" />
            <label style={label}>Dirección</label>
            <input type="text" value={datos.direccion} disabled={guardando} maxLength={200}
              onChange={(e) => set('direccion', e.target.value)} style={{ ...input, marginBottom: '10px' }} placeholder="Ej: Barrio El Centro, Olanchito, Yoro" />
            <label style={label}>Correo del negocio</label>
            <input type="email" value={datos.correo} disabled={guardando} maxLength={120}
              onChange={(e) => set('correo', e.target.value)} style={{ ...input, marginBottom: '10px' }} placeholder="Ej: ventas@minegocio.com" />
            <label style={label}>Nota al pie</label>
            <input type="text" value={datos.pie_comprobante} disabled={guardando} maxLength={160}
              onChange={(e) => set('pie_comprobante', e.target.value)} style={input} placeholder="Ej: Cambios dentro de 7 días con este comprobante." />
          </div>

          <FacturacionConfig datosNegocio={datos} />

          <div style={{ marginBottom: '16px' }}>
            <label style={label}>🔗 Nombre corto (enlace del catálogo)</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ fontSize: '13px', color: '#9CA3AF', whiteSpace: 'nowrap' }}>kontrol.automatikaia.com/</span>
              <input type="text" value={datos.slug} disabled={guardando}
                onChange={(e) => set('slug', e.target.value)} style={{ ...input, flex: 1, minWidth: '90px' }} placeholder="lolitas" />
            </div>
            {normalizarSlug(datos.slug) ? (
              <p style={{ margin: '6px 0 0', fontSize: '12px', color: '#059669', fontWeight: 600 }}>
                ✅ Tu catálogo: kontrol.automatikaia.com/{normalizarSlug(datos.slug)}
              </p>
            ) : (
              <p style={{ margin: '6px 0 0', fontSize: '12px', color: '#9CA3AF' }}>
                Elige un nombre corto para compartir tu catálogo con un enlace lindo. Solo letras, números y guiones (ej. mi-tienda).
              </p>
            )}
          </div>

          <DisenoTiendaConfig datos={datos} set={set} guardando={guardando}
            portadaMostrada={portadaPreview || datos.portada_url}
            onElegirPortada={(f) => { if (f) { setPortadaFile(f); setPortadaPreview(URL.createObjectURL(f)); } }}
            onQuitarPortada={() => { setPortadaFile(null); setPortadaPreview(null); set('portada_url', null); }} />

          <EnvioTiendaConfig envio={datos.tienda_envio} onCambiar={(v) => set('tienda_envio', v)} guardando={guardando}
            pago={datos.tienda_pago} onCambiarPago={(v) => set('tienda_pago', v)} />

          <CombosTiendaConfig combos={datos.tienda_combos} onCambiar={(v) => set('tienda_combos', v)} guardando={guardando} />

          <div style={{ marginBottom: '16px', padding: '14px', borderRadius: '10px', border: '1px solid #F3F4F6', backgroundColor: datos.catalogo_publico ? '#F0FDF4' : '#F9FAFB' }}>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: guardando ? 'not-allowed' : 'pointer' }}>
              <input type="checkbox" checked={!!datos.catalogo_publico} disabled={guardando}
                onChange={(e) => set('catalogo_publico', e.target.checked)}
                style={{ width: '20px', height: '20px', marginTop: '2px', accentColor: '#059669', cursor: 'inherit' }} />
              <span>
                <span style={{ ...label, margin: 0 }}>🌎 Catálogo público {datos.catalogo_publico ? '(activado)' : '(desactivado)'}</span>
                <span style={{ display: 'block', margin: '4px 0 0', fontSize: '12px', color: '#6B7280' }}>
                  Cuando está activado, cualquiera con tu enlace puede ver tu catálogo para comprarte.
                  Cuando está desactivado, tu inventario queda <b>privado</b> y solo lo ves tú al iniciar sesión.
                </span>
              </span>
            </label>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={label}>📊 Píxel de Meta (opcional)</label>
            <input type="text" inputMode="numeric" value={datos.meta_pixel_id} disabled={guardando}
              onChange={(e) => set('meta_pixel_id', e.target.value.replace(/[^0-9]/g, ''))} style={input} placeholder="Ej: 975678811638322" />
            <p style={{ margin: '5px 0 0', fontSize: '12px', color: '#9CA3AF' }}>
              Si haces anuncios en Facebook o Instagram, pega aquí el número de tu píxel (conjunto de datos) del Administrador de eventos de Meta.
              Así Meta sabe qué productos ven tus clientas y cuándo te hacen un pedido. Déjalo vacío si no lo usas.
            </p>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={label}>Comisión bancaria POS / Link de pago (%)</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <CampoNumero valor={datos.comision_pos_pct} onChange={(v) => set('comision_pos_pct', v)} deshabilitado={guardando}
                decimales={1} min={0} max={100} maxMensaje="La comisión no puede pasar del 100%"
                placeholder="4" estilo={{ ...input, maxWidth: '120px' }} />
              <span style={{ fontSize: '15px', color: '#9CA3AF', fontWeight: 600 }}>%</span>
            </div>
            <p style={{ margin: '5px 0 0', fontSize: '12px', color: '#9CA3AF' }}>Se rebaja como gasto "Comisiones bancarias" cuando cobras marcando 💳 POS/Link. Cámbialo según tu banco (por defecto 4%).</p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '22px' }}>
            <div>
              <label style={label}>País</label>
              <select value={datos.pais || ''} disabled={guardando} onChange={(e) => elegirPais(e.target.value)} style={input}>
                <option value="">Elige tu país…</option>
                {datos.pais && !PAISES.includes(datos.pais) && <option value={datos.pais}>{datos.pais}</option>}
                {PAISES.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label style={label}>Moneda</label>
              <select value={datos.moneda} disabled={guardando} onChange={(e) => set('moneda', e.target.value)} style={input}>
                {datos.moneda && !MONEDAS.some((m) => m.codigo === datos.moneda) && <option value={datos.moneda}>{monedaLabel(datos.moneda)}</option>}
                {MONEDAS.map((m) => <option key={m.codigo} value={m.codigo}>{m.label}</option>)}
              </select>
            </div>
          </div>

          <div style={{ marginBottom: '22px' }}>
            <label style={label}>Zona horaria</label>
            <select value={datos.zona_horaria || 'America/Tegucigalpa'} disabled={guardando} onChange={(e) => set('zona_horaria', e.target.value)} style={input}>
              {ZONAS_HORARIAS.map((z) => <option key={z.tz} value={z.tz}>{z.label}</option>)}
              {datos.zona_horaria && !ZONAS_HORARIAS.some((z) => z.tz === datos.zona_horaria) && <option value={datos.zona_horaria}>{datos.zona_horaria}</option>}
            </select>
            <p style={{ margin: '5px 0 0', fontSize: '12px', color: '#9CA3AF' }}>Define a qué día pertenece cada venta (para el Corte del día y el Cierre de mes). Por defecto Honduras.</p>
          </div>

          <div style={{ marginBottom: '18px' }}>
            <label style={label}>¿Qué vendes?</label>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {OPCIONES_VENDE.map((o) => (
                <button key={o.clave} type="button" disabled={guardando} aria-pressed={datos.vende === o.clave} onClick={() => set('vende', o.clave)}
                  style={{ padding: '9px 14px', borderRadius: '999px', fontSize: '13.5px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
                    border: `1.5px solid ${datos.vende === o.clave ? 'var(--color-primario,#DB2777)' : '#E5E7EB'}`,
                    background: datos.vende === o.clave ? '#FDF2F8' : '#fff', color: '#111827' }}>
                  {o.emoji} {o.label}
                </button>
              ))}
            </div>
            <p style={{ margin: '5px 0 0', fontSize: '12px', color: '#9CA3AF' }}>Solo cambia lo que ves en el menú: {OPCIONES_VENDE.find((o) => o.clave === datos.vende)?.ayuda} No se borra nada; lo puedes cambiar cuando quieras.</p>
          </div>

          <div style={{ marginBottom: '18px' }}>
            <label style={label} htmlFor="config-rubro">Tipo de negocio</label>
            <select id="config-rubro" value={datos.rubro || ''} disabled={guardando} onChange={(e) => set('rubro', e.target.value)} style={input}>
              <option value="">Sin elegir</option>
              {RUBROS.map((r) => <option key={r.clave} value={r.clave}>{r.emoji} {r.label}</option>)}
            </select>
            <p style={{ margin: '5px 0 0', fontSize: '12px', color: '#9CA3AF' }}>Activa las herramientas de tu rubro (ej. 🚚 Distribuidor: productos por libra) y ayuda a la IA a escribir para tu tipo de negocio.</p>
          </div>

          <div style={{ marginBottom: '22px', padding: '14px', borderRadius: '10px', border: '1px solid #F3F4F6', backgroundColor: datos.modo_viajes ? '#F5F3FF' : '#F9FAFB' }}>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: guardando ? 'not-allowed' : 'pointer' }}>
              <input type="checkbox" checked={!!datos.modo_viajes} disabled={guardando}
                onChange={(e) => set('modo_viajes', e.target.checked)}
                style={{ width: '20px', height: '20px', marginTop: '2px', accentColor: '#7C3AED', cursor: 'inherit' }} />
              <span>
                <span style={{ ...label, margin: 0 }}>✈️ Soy agencia de viajes</span>
                <span style={{ display: 'block', margin: '4px 0 0', fontSize: '12px', color: '#6B7280' }}>
                  Activa los campos de <b>destino</b> y <b>servicio</b> en la ficha de tus clientes, para registrar y filtrar qué compró cada uno.
                </span>
              </span>
            </label>
          </div>

          <button onClick={guardar} disabled={guardando}
            style={{ width: '100%', padding: '14px', backgroundColor: guardando ? '#9CA3AF' : 'var(--color-primario,#DB2777)', color: 'white', border: 'none', borderRadius: '10px', fontSize: '16px', fontWeight: 'bold', cursor: guardando ? 'not-allowed' : 'pointer' }}>
            {guardando ? '⏳ Guardando...' : '💾 Guardar cambios'}
          </button>

          <p style={{ margin: '16px 0 0', fontSize: '12px', color: '#9CA3AF', textAlign: 'center' }}>
            Tu correo de inicio de sesión no se cambia desde aquí (es un dato de seguridad de la cuenta).
          </p>
        </div>
      )}
    </div>
  );
}
